import { HStack, Tabs, TabList, Tab } from '@cdl/heartbeat-react';
import { useNavigate } from 'react-router-dom';
import React, { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Portal } from '../../../shared/components/Portal';
import { sendAnalyticsPage } from '../../analytics/thunks/sendAnalyticsPage.thunk';
import { hasILRImageSelector } from '../../layout/selectors/layout.selectors';
import { rawSignalLeadNamesSelector } from '../../raw-signal/selectors/raw-signal.selectors';
import { RESTING_LEAD_COUNT } from '../../resting/resting.util';
import {
  getApplicationPath,
  setDefaultPathIndex,
} from '../../ecg/utils/application-path.utils';
import { useHeaderRef } from '../hooks/useHeaderRef';
import { leadCountSelector } from '../selectors/signal.selectors';
import { NavigationTab } from '../types/application-path.types';
import { Next } from './Next';
import { SaveButtonsBar } from './SaveButtonsBar';
import { annotationTaskSelector } from '../selectors/annotation-task.selectors';
import { AnnotationTaskInfo } from './AnnotationTaskInfo';

interface Props {
  ecgId: string;
}

export const DelineationHeader = ({ ecgId }: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const headerRef = useHeaderRef();
  const hasILRImage = useSelector(hasILRImageSelector);
  const path = getApplicationPath(window.location);
  const leadNames = useSelector(rawSignalLeadNamesSelector);
  const leadCount = useSelector(leadCountSelector);
  const tabs = useMemo<NavigationTab[]>(() => {
    return [
      { label: 'HR', path: 'hr' },
      { label: 'Zoom', path: 'zoom' },
      { label: 'Noise', path: 'noise' },
      { label: 'Zoom Noise', path: 'zoom-noise' },
      ...(leadCount === RESTING_LEAD_COUNT
        ? ([{ label: 'Resting', path: 'resting' }] as NavigationTab[])
        : []),
      ...(leadNames.length !== 0
        ? ([{ label: 'Raw', path: 'raw' }] as NavigationTab[])
        : []),
      ...(hasILRImage
        ? ([{ label: 'ILR', path: 'ilr' }] as NavigationTab[])
        : []),
    ];
  }, [leadCount, leadNames, hasILRImage]);
  const navigatePath = useCallback(
    (tabIndex: number) => {
      const path = tabs[tabIndex]?.path || 'hr';
      dispatch(sendAnalyticsPage(`page:${path}`));
      navigate(`/${ecgId}/${path}`);
    },
    [navigate, dispatch, tabs, ecgId],
  );
  const defaultTabIndex = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    () => setDefaultPathIndex(tabs, path!),
    [tabs, path],
  );
  const annotationTask = useSelector(annotationTaskSelector);

  if (path == null) {
    return null;
  }

  return (
    <Portal to={headerRef}>
      <HStack flex={1} justifyContent='space-between'>
        <Tabs onChange={navigatePath} index={defaultTabIndex}>
          <TabList mb='-s' border='0'>
            {tabs.map((tab) => (
              <Tab key={tab.label}>{tab.label}</Tab>
            ))}
          </TabList>
        </Tabs>
        <HStack spacing='m'>
          <HStack>
            <SaveButtonsBar />
            <Next />
          </HStack>
          {annotationTask && (
            <AnnotationTaskInfo
              title={annotationTask.name}
              description={annotationTask.description}
            />
          )}
        </HStack>
      </HStack>
    </Portal>
  );
};
